import { useEffect, useState } from "react";
import { KeyRound, Mail, Trash2 } from "lucide-react";
import { formatDate } from "@mavatech/shared";
import { Button, Input, Select } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

export function IntegrationsTab() {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);

  // ── SMTP ───────────────────────────────────────────────────
  const smtp = trpc.organization.smtpConfig.useQuery();
  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [secure, setSecure] = useState("false");
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [from, setFrom] = useState("");

  useEffect(() => {
    if (!smtp.data?.configured) return;
    setHost(smtp.data.host ?? "");
    setPort(String(smtp.data.port ?? 587));
    setSecure(String(smtp.data.secure ?? false));
    setUser(smtp.data.user ?? "");
    setFrom(smtp.data.from ?? "");
  }, [smtp.data]);

  const saveSmtp = trpc.organization.setSmtpConfig.useMutation({
    onSuccess: () => {
      utils.organization.smtpConfig.invalidate();
      utils.invoice.emailChannel.invalidate();
      setPassword("");
      addToast({ type: "success", title: "SMTP gespeichert" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const clearSmtp = trpc.organization.clearSmtpConfig.useMutation({
    onSuccess: () => {
      utils.organization.smtpConfig.invalidate();
      utils.invoice.emailChannel.invalidate();
      setHost("");
      setUser("");
      setPassword("");
      setFrom("");
      addToast({ type: "success", title: "SMTP entfernt" });
    },
  });

  // ── API-Schlüssel ──────────────────────────────────────────
  const keys = trpc.apiKey.list.useQuery();
  const [keyName, setKeyName] = useState("");
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const createKey = trpc.apiKey.create.useMutation({
    onSuccess: (r) => {
      setFreshToken(r.token);
      setKeyName("");
      utils.apiKey.list.invalidate();
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const revokeKey = trpc.apiKey.revoke.useMutation({
    onSuccess: () => utils.apiKey.list.invalidate(),
  });

  return (
    <div className="p-6 max-w-3xl space-y-8">
      <section>
        <h3 className="text-sm font-semibold text-text-primary mb-1 flex items-center gap-2">
          <Mail className="w-4 h-4" /> E-Mail-Versand (SMTP)
        </h3>
        <p className="text-xs text-text-tertiary mb-3">
          Optional. Der Versand direkt aus Angebot/Rechnung ist bis zur
          Anbindung hier vorübergehend deaktiviert — bis dahin gibt es dort nur
          den PDF-Download.
          {smtp.data?.configured &&
            smtp.data.source === "env" &&
            " Aktuell aktiv: globale Serverkonfiguration."}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Host"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="smtp.example.com"
          />
          <Input
            label="Port"
            type="number"
            value={port}
            onChange={(e) => setPort(e.target.value)}
          />
          <Select
            label="Verschlüsselung"
            value={secure}
            onChange={(e) => setSecure(e.target.value)}
            options={[
              { value: "false", label: "STARTTLS / keine (Port 587/25)" },
              { value: "true", label: "SSL/TLS (Port 465)" },
            ]}
          />
          <Input
            label="Absender (From)"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            placeholder="rechnung@…"
          />
          <Input
            label="Benutzer"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            autoComplete="off"
          />
          <Input
            label={
              smtp.data?.configured && smtp.data.hasPassword
                ? "Passwort (leer = unverändert)"
                : "Passwort"
            }
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="mt-4 flex gap-2">
          <Button
            variant="primary"
            loading={saveSmtp.isPending}
            onClick={() =>
              saveSmtp.mutate({
                host,
                port: Number.parseInt(port, 10) || 587,
                secure: secure === "true",
                user: user || undefined,
                password: password || undefined,
                from: from || undefined,
              })
            }
          >
            SMTP speichern
          </Button>
          {smtp.data?.configured && smtp.data.source === "org" && (
            <Button
              variant="secondary"
              loading={clearSmtp.isPending}
              onClick={() => clearSmtp.mutate()}
            >
              Entfernen
            </Button>
          )}
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-text-primary mb-1 flex items-center gap-2">
          <KeyRound className="w-4 h-4" /> API-Schlüssel
        </h3>
        <p className="text-xs text-text-tertiary mb-3">
          Nur-lesender Zugriff über <code>/api/v1</code> (Kunden, Angebote,
          Rechnungen) — Grundlage für eine spätere Baustellen-App. Als{" "}
          <code>Authorization: Bearer …</code> senden.
        </p>
        <div className="flex items-end gap-2">
          <Input
            label="Bezeichnung"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="z. B. Monteur-iPad"
            className="flex-1"
          />
          <Button
            variant="secondary"
            loading={createKey.isPending}
            disabled={keyName.trim().length < 2}
            onClick={() => createKey.mutate({ name: keyName.trim() })}
          >
            Erstellen
          </Button>
        </div>
        {freshToken && (
          <div className="mt-3 card p-3 bg-bg-tertiary">
            <p className="text-xs text-text-secondary mb-1">
              Schlüssel wird nur jetzt angezeigt — bitte sicher speichern:
            </p>
            <code className="text-xs break-all text-text-primary">
              {freshToken}
            </code>
          </div>
        )}
        {!!keys.data?.length && (
          <div className="mt-3 space-y-1.5">
            {keys.data.map((k) => (
              <div
                key={k.id}
                className="flex items-center justify-between card p-2.5 text-sm"
              >
                <span className="text-text-secondary">
                  {k.name}{" "}
                  <span className="text-text-tertiary text-xs">
                    · {k.prefix}…
                  </span>
                  {k.revokedAt && (
                    <span className="text-danger text-xs"> · gesperrt</span>
                  )}
                </span>
                <span className="flex items-center gap-3 text-text-tertiary text-xs">
                  {k.lastUsedAt
                    ? `zuletzt ${formatDate(k.lastUsedAt)}`
                    : "ungenutzt"}
                  {!k.revokedAt && (
                    <button
                      className="text-danger hover:opacity-80"
                      onClick={() => revokeKey.mutate({ id: k.id })}
                      title="Sperren"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
